import React from 'react';
import Icon from './icons';
import { useSch } from './context';

/**
 * Step 3 "on which screens": all / groups / hand-picked screens. Search shows above 8 screens (the prototype's rule), the
 * checklist is grouped with a select-all per group, and a selection bar lists up to 8 picks as removable tags.
 */
export default function ScreenPicker({ where, q, onMode, onGroups, onScreens, onQuery }) {
  const { t, f, groups, screens, screenName } = useSch();
  const realGroups = groups.filter((g) => g.id !== null);
  const ids = where.ids;
  const needle = (q || '').trim();

  const visible = groups
    .map((g) => ({ g, rows: g.screens.filter((s) => !needle || s.name.indexOf(needle) >= 0 || (g.name || t('sch6.ungrouped')).indexOf(needle) >= 0) }))
    .filter((x) => x.rows.length);

  const toggleScreen = (id, on) => onScreens(on ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((x) => x !== id));
  const toggleGroupAll = (rows) => {
    const rowIds = rows.map((s) => s.id);
    const all = rowIds.every((id) => ids.includes(id));
    onScreens(all ? ids.filter((id) => !rowIds.includes(id)) : [...ids, ...rowIds.filter((id) => !ids.includes(id))]);
  };
  const toggleGroup = (id, on) => onGroups(on ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((x) => x !== id));

  const mode = (m, icon, title, desc) => (
    <button type="button" className="sch6-bigopt" aria-pressed={where.mode === m} onClick={() => onMode(m)}>
      <span className="sch6-ic"><Icon name={icon} /></span><b>{title}</b><span>{desc}</span>
    </button>
  );

  return (
    <>
      <p className="sch6-q">{t('sch6.s3_q')}</p>
      <div className="sch6-modes">
        {mode('all', 'screen', t('sch6.m_all'), t('sch6.m_all_d', { total: f.nScreens(screens.length) }))}
        {mode('groups', 'layers', t('sch6.m_groups'), t('sch6.m_groups_d', { name: realGroups[0]?.name ?? '' }))}
        {mode('screens', 'list', t('sch6.m_screens'), t('sch6.m_screens_d'))}
      </div>
      {where.mode === 'groups' && (
        <>
          <div className="sch6-checklist">
            {realGroups.map((g) => (
              <label key={g.id} className="sch6-crow">
                <input type="checkbox" checked={ids.includes(g.id)} onChange={(e) => toggleGroup(g.id, e.target.checked)} /> {g.name}<small>{f.nScreens(g.screens.length)}</small>
              </label>
            ))}
          </div>
          {ids.length > 0 && <p className="sch6-hint">{t('sch6.g_follow')}</p>}
        </>
      )}
      {where.mode === 'screens' && (
        <>
          <div className="sch6-selbar">
            {!ids.length ? <span className="sch6-hint">{t('sch6.none_sel')}</span> : (
              <>
                <span className="sch6-cnt">{f.nScreens(ids.length)}</span>
                {ids.length <= 8 && ids.map((id) => (
                  <span key={id} className="sch6-tag2">{screenName(id)}
                    <button type="button" onClick={() => toggleScreen(id, false)} aria-label={t('sch6.remove_aria', { name: screenName(id) })}><Icon name="x" /></button>
                  </span>
                ))}
                <button type="button" className="sch6-btn sch6-ghost" style={{ padding: '2px 8px' }} onClick={() => onScreens([])}>{t('sch6.clear')}</button>
              </>
            )}
          </div>
          {screens.length > 8 && (
            <div className="sch6-search">
              <Icon name="search" />
              <input className="sch6-inp" placeholder={t('sch6.search_ph')} value={q || ''} autoComplete="off" onChange={(e) => onQuery(e.target.value)} />
            </div>
          )}
          <div className="sch6-checklist">
            {visible.length === 0 && <p className="sch6-hint" style={{ padding: 12 }}>{t('sch6.no_match')}</p>}
            {visible.map(({ g, rows }) => {
              const all = rows.every((s) => ids.includes(s.id));
              return (
                <React.Fragment key={g.id ?? 'none'}>
                  <div className="sch6-ghead"><span>{`${g.name ?? t('sch6.ungrouped')} · ${f.num(rows.length)}`}</span>
                    <button type="button" onClick={() => toggleGroupAll(rows)}>{all ? t('sch6.deselect_all') : t('sch6.select_all')}</button>
                  </div>
                  {rows.map((s) => (
                    <label key={s.id} className="sch6-crow">
                      <input type="checkbox" checked={ids.includes(s.id)} onChange={(e) => toggleScreen(s.id, e.target.checked)} /> {s.name}
                    </label>
                  ))}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
